import React, { useState, useEffect, useMemo } from 'react';
import { supabase } from '../../../lib/supabase';
import { orderService } from '../../../services/supabaseService';

const STATUS_OPTIONS = ['all', 'pending', 'accepted', 'in_progress', 'completed', 'cancelled'];
const VEHICLE_OPTIONS = ['all', 'bike', 'boda', 'tuktuk', 'car'];
const SORT_OPTIONS = [
  { value: 'newest', label: 'Newest First' },
  { value: 'oldest', label: 'Oldest First' },
  { value: 'fare_high', label: 'Fare: High → Low' },
  { value: 'fare_low', label: 'Fare: Low → High' },
];

const STATUS_COLORS = {
  pending: { bg: 'bg-yellow-500/15', text: 'text-yellow-400', dot: 'bg-yellow-400' },
  accepted: { bg: 'bg-blue-500/15', text: 'text-blue-400', dot: 'bg-blue-400' },
  in_progress: { bg: 'bg-purple-500/15', text: 'text-purple-400', dot: 'bg-purple-400' },
  completed: { bg: 'bg-green-500/15', text: 'text-green-400', dot: 'bg-green-400' },
  cancelled: { bg: 'bg-red-500/15', text: 'text-red-400', dot: 'bg-red-400' },
};

const VEHICLE_ICONS = {
  bike: '🚲',
  boda: '🏍️',
  tuktuk: '🛺',
  car: '🚗',
};

const OrderHistorySection = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [sortBy, setSortBy] = useState('newest');
  const [statusFilter, setStatusFilter] = useState('all');
  const [vehicleFilter, setVehicleFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        const { data: { user } } = await supabase?.auth?.getUser();
        if (user) {
          const data = await orderService?.getCustomerOrders(user?.id);
          setOrders(data || []);
        }
      } catch (err) {
        console.error('Failed to fetch order history:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchOrders();
  }, []);

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (sortBy !== 'newest') count++;
    if (statusFilter !== 'all') count++;
    if (vehicleFilter !== 'all') count++;
    if (dateFrom) count++;
    if (dateTo) count++;
    return count;
  }, [sortBy, statusFilter, vehicleFilter, dateFrom, dateTo]);

  const filteredOrders = useMemo(() => {
    let result = [...orders];

    if (statusFilter !== 'all') {
      result = result?.filter(o => o?.status === statusFilter);
    }
    if (vehicleFilter !== 'all') {
      result = result?.filter(o => o?.vehicleType === vehicleFilter);
    }
    if (dateFrom) {
      const from = new Date(dateFrom);
      result = result?.filter(o => new Date(o?.createdAt) >= from);
    }
    if (dateTo) {
      const to = new Date(dateTo);
      to?.setHours(23, 59, 59, 999);
      result = result?.filter(o => new Date(o?.createdAt) <= to);
    }

    switch (sortBy) {
      case 'oldest':
        result?.sort((a, b) => new Date(a?.createdAt) - new Date(b?.createdAt));
        break;
      case 'fare_high':
        result?.sort((a, b) => (b?.totalAmount || 0) - (a?.totalAmount || 0));
        break;
      case 'fare_low':
        result?.sort((a, b) => (a?.totalAmount || 0) - (b?.totalAmount || 0));
        break;
      default:
        result?.sort((a, b) => new Date(b?.createdAt) - new Date(a?.createdAt));
    }

    return result;
  }, [orders, sortBy, statusFilter, vehicleFilter, dateFrom, dateTo]);

  const resetFilters = () => {
    setSortBy('newest');
    setStatusFilter('all');
    setVehicleFilter('all');
    setDateFrom('');
    setDateTo('');
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    const d = new Date(dateStr);
    return d?.toLocaleDateString('en-KE', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d?.toLocaleTimeString('en-KE', { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="space-y-3">
      {/* Section Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-bold text-[#0a1628]/70 uppercase tracking-wider">Order History</h3>
        <button
          onClick={() => setShowFilters(prev => !prev)}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold transition-all border ${
            showFilters || activeFilterCount > 0
              ? 'bg-[#0a1628] text-[#ffd700] border-[#0a1628]'
              : 'bg-white text-[#0a1628]/60 border-[#0a1628]/15 hover:border-[#0a1628]/30'
          }`}
        >
          <svg viewBox="0 0 24 24" fill="currentColor" className="w-3.5 h-3.5">
            <path d="M4.25 5.61C6.27 8.2 10 13 10 13v6c0 .55.45 1 1 1h2c.55 0 1-.45 1-1v-6s3.72-4.8 5.74-7.39A.998.998 0 0019 4H5a1 1 0 00-.75 1.61z"/>
          </svg>
          Filters
          {activeFilterCount > 0 && (
            <span className="w-4 h-4 rounded-full bg-[#ffd700] text-[#0a1628] text-xs font-black flex items-center justify-center leading-none">
              {activeFilterCount}
            </span>
          )}
        </button>
      </div>
      {/* Filters Panel */}
      {showFilters && (
        <div className="bg-white rounded-2xl border border-[#0a1628]/10 p-4 space-y-4 shadow-sm">
          {/* Sort */}
          <div>
            <p className="text-xs font-semibold text-[#0a1628]/50 uppercase tracking-wider mb-2">Sort By</p>
            <div className="grid grid-cols-2 gap-2">
              {SORT_OPTIONS?.map(opt => (
                <button
                  key={opt?.value}
                  onClick={() => setSortBy(opt?.value)}
                  className={`py-2 px-3 rounded-xl text-xs font-semibold transition-all border ${
                    sortBy === opt?.value
                      ? 'bg-[#0a1628] text-[#ffd700] border-[#0a1628]'
                      : 'bg-[#0a1628]/4 text-[#0a1628]/60 border-transparent hover:border-[#0a1628]/15'
                  }`}
                >
                  {opt?.label}
                </button>
              ))}
            </div>
          </div>

          {/* Status Filter */}
          <div>
            <p className="text-xs font-semibold text-[#0a1628]/50 uppercase tracking-wider mb-2">Status</p>
            <div className="flex flex-wrap gap-2">
              {STATUS_OPTIONS?.map(s => (
                <button
                  key={s}
                  onClick={() => setStatusFilter(s)}
                  className={`py-1.5 px-3 rounded-full text-xs font-semibold capitalize transition-all border ${
                    statusFilter === s
                      ? 'bg-[#0a1628] text-[#ffd700] border-[#0a1628]'
                      : 'bg-[#0a1628]/4 text-[#0a1628]/60 border-transparent hover:border-[#0a1628]/15'
                  }`}
                >
                  {s === 'all' ? 'All' : s?.replace('_', ' ')}
                </button>
              ))}
            </div>
          </div>

          {/* Vehicle Type Filter */}
          <div>
            <p className="text-xs font-semibold text-[#0a1628]/50 uppercase tracking-wider mb-2">Vehicle Type</p>
            <div className="flex flex-wrap gap-2">
              {VEHICLE_OPTIONS?.map(v => (
                <button
                  key={v}
                  onClick={() => setVehicleFilter(v)}
                  className={`py-1.5 px-3 rounded-full text-xs font-semibold capitalize transition-all border ${
                    vehicleFilter === v
                      ? 'bg-[#0a1628] text-[#ffd700] border-[#0a1628]'
                      : 'bg-[#0a1628]/4 text-[#0a1628]/60 border-transparent hover:border-[#0a1628]/15'
                  }`}
                >
                  {v === 'all' ? 'All' : `${VEHICLE_ICONS?.[v] || ''} ${v?.charAt(0)?.toUpperCase() + v?.slice(1)}`}
                </button>
              ))}
            </div>
          </div>

          {/* Date Range */}
          <div>
            <p className="text-xs font-semibold text-[#0a1628]/50 uppercase tracking-wider mb-2">Date Range</p>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs text-[#0a1628]/40 mb-1 block">From</label>
                <input
                  type="date"
                  value={dateFrom}
                  onChange={e => setDateFrom(e?.target?.value)}
                  className="w-full text-xs px-3 py-2 bg-[#0a1628]/4 border border-[#0a1628]/10 rounded-xl focus:outline-none focus:border-[#ffd700] focus:ring-1 focus:ring-[#ffd700]/30 text-[#0a1628]"
                />
              </div>
              <div>
                <label className="text-xs text-[#0a1628]/40 mb-1 block">To</label>
                <input
                  type="date"
                  value={dateTo}
                  onChange={e => setDateTo(e?.target?.value)}
                  className="w-full text-xs px-3 py-2 bg-[#0a1628]/4 border border-[#0a1628]/10 rounded-xl focus:outline-none focus:border-[#ffd700] focus:ring-1 focus:ring-[#ffd700]/30 text-[#0a1628]"
                />
              </div>
            </div>
          </div>

          {/* Reset */}
          {activeFilterCount > 0 && (
            <button
              onClick={resetFilters}
              className="w-full py-2 rounded-xl text-xs font-semibold text-red-500 bg-red-50 border border-red-100 hover:bg-red-100 transition-colors"
            >
              Reset All Filters
            </button>
          )}
        </div>
      )}
      {/* Results */}
      <div className="bg-white rounded-2xl border border-[#0a1628]/10 overflow-hidden shadow-sm">
        {loading ? (
          <div className="p-4 space-y-3">
            {[...Array(3)]?.map((_, i) => (
              <div key={i} className="flex items-center gap-3 animate-pulse">
                <div className="w-10 h-10 rounded-xl bg-[#0a1628]/8 flex-shrink-0" />
                <div className="flex-1 space-y-2">
                  <div className="h-3 w-32 rounded-full bg-[#0a1628]/8" />
                  <div className="h-2.5 w-48 rounded-full bg-[#0a1628]/6" />
                </div>
                <div className="h-4 w-16 rounded-full bg-[#0a1628]/8" />
              </div>
            ))}
          </div>
        ) : filteredOrders?.length === 0 ? (
          <div className="py-10 text-center">
            <div className="w-12 h-12 rounded-2xl bg-[#0a1628]/6 flex items-center justify-center mx-auto mb-3">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-6 h-6 text-[#0a1628]/30">
                <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
            </div>
            <p className="text-sm font-semibold text-[#0a1628]/40">
              {orders?.length === 0 ? 'No orders yet' : 'No orders match filters'}
            </p>
            {activeFilterCount > 0 && (
              <button onClick={resetFilters} className="mt-2 text-xs text-[#0a1628]/50 underline">Clear filters</button>
            )}
          </div>
        ) : (
          <div className="divide-y divide-[#0a1628]/6">
            {activeFilterCount > 0 && (
              <div className="px-4 py-2 bg-[#0a1628]/3 flex items-center justify-between">
                <span className="text-xs text-[#0a1628]/50">{filteredOrders?.length} result{filteredOrders?.length !== 1 ? 's' : ''}</span>
                <button onClick={resetFilters} className="text-xs text-[#0a1628]/40 hover:text-red-500 transition-colors">Clear</button>
              </div>
            )}
            {filteredOrders?.map(order => {
              const statusStyle = STATUS_COLORS?.[order?.status] || STATUS_COLORS?.pending;
              const isExpanded = expandedId === order?.id;
              return (
                <div key={order?.id}>
                  <button
                    onClick={() => setExpandedId(isExpanded ? null : order?.id)}
                    className="w-full px-4 py-3.5 flex items-center gap-3 hover:bg-[#0a1628]/2 transition-colors text-left"
                  >
                    <div className="w-10 h-10 rounded-xl bg-[#0a1628]/6 flex items-center justify-center flex-shrink-0 text-lg">
                      {VEHICLE_ICONS?.[order?.vehicleType] || '🚗'}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className="text-xs font-bold text-[#0a1628]">{order?.orderNumber || `#${order?.id?.slice(0, 8)}`}</span>
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${statusStyle?.bg} ${statusStyle?.text}`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${statusStyle?.dot}`} />
                          {order?.status?.replace('_', ' ')}
                        </span>
                      </div>
                      <p className="text-xs text-[#0a1628]/50 truncate">{order?.pickupAddress} → {order?.dropoffAddress}</p>
                      <p className="text-xs text-[#0a1628]/35 mt-0.5">{formatDate(order?.createdAt)} · {formatTime(order?.createdAt)}</p>
                    </div>
                    <div className="flex flex-col items-end gap-1 flex-shrink-0">
                      <span className="text-sm font-bold text-[#0a1628]">KSh {order?.totalAmount?.toLocaleString() || '—'}</span>
                      <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        className={`w-3.5 h-3.5 text-[#0a1628]/30 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                      >
                        <path d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </button>
                  {isExpanded && (
                    <div className="px-4 pb-4 bg-[#0a1628]/2 border-t border-[#0a1628]/6">
                      <div className="pt-3 grid grid-cols-2 gap-x-4 gap-y-2">
                        <div>
                          <p className="text-xs text-[#0a1628]/40">Service</p>
                          <p className="text-xs font-semibold text-[#0a1628] capitalize">{order?.serviceType || '—'}</p>
                        </div>
                        <div>
                          <p className="text-xs text-[#0a1628]/40">Vehicle</p>
                          <p className="text-xs font-semibold text-[#0a1628] capitalize">{order?.vehicleType || '—'}</p>
                        </div>
                        <div>
                          <p className="text-xs text-[#0a1628]/40">Distance</p>
                          <p className="text-xs font-semibold text-[#0a1628]">{order?.distanceKm ? `${order?.distanceKm} km` : '—'}</p>
                        </div>
                        <div>
                          <p className="text-xs text-[#0a1628]/40">Platform Fee</p>
                          <p className="text-xs font-semibold text-[#0a1628]">KSh {order?.platformFee || '—'}</p>
                        </div>
                        {order?.pickupAddress && (
                          <div className="col-span-2">
                            <p className="text-xs text-[#0a1628]/40">Pickup</p>
                            <p className="text-xs font-semibold text-[#0a1628]">{order?.pickupAddress}</p>
                          </div>
                        )}
                        {order?.dropoffAddress && (
                          <div className="col-span-2">
                            <p className="text-xs text-[#0a1628]/40">Drop-off</p>
                            <p className="text-xs font-semibold text-[#0a1628]">{order?.dropoffAddress}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default OrderHistorySection;
